/**
 * Bottle Quiz v2 - confidence-based bottle matcher.
 * Uses native <dialog> for modals (top-layer, immune to stacking contexts).
 * Edit bottle-quiz-data.json to add bottles or questions.
 */

if (!customElements.get('bottle-quiz')) {
  customElements.define(
    'bottle-quiz',
    class BottleQuiz extends HTMLElement {
      constructor() {
        super();
        this.data = null;
        this.answers = {};
        this.history = [];
        this.scores = {};
        this.questionIndex = 0;
        this.lang = (window.Shopify && window.Shopify.locale) ? window.Shopify.locale : 'en';
      }

      i(val) {
        if (val && typeof val === 'object' && !Array.isArray(val) && (val.en || val.nl || val.de)) {
          return val[this.lang] || val.en || '';
        }
        return val || '';
      }

      connectedCallback() {
        var wrapper = this.closest('.bottle-quiz-wrapper');
        this.browseOnly = this.dataset.mode === 'browse';

        this.els = {
          steps: this.querySelector('.bottle-quiz__steps'),
          progress: this.querySelector('.bottle-quiz__progress-bar'),
          back: this.querySelector('.bottle-quiz__back'),
          help: this.querySelector('.bottle-quiz__help-link'),
          result: this.querySelector('.bottle-quiz__result'),
          candidates: this.querySelector('.bottle-quiz__candidates')
        };

        /* ── Help modal (<dialog>) ── */
        this.helpDialog = wrapper ? wrapper.querySelector('.bq-modal--help') : null;
        if (this.helpDialog) {
          this._initDialog(this.helpDialog);
          var chatBtn = this.helpDialog.querySelector('.bq-modal__chat');
          if (chatBtn) {
            chatBtn.addEventListener('click', () => {
              this.helpDialog.close();
              this.openInbox();
            });
          }
        }

        /* ── Lightbox modal (<dialog>) ── */
        this.lbDialog = wrapper ? wrapper.querySelector('.bq-modal--lightbox') : null;
        if (this.lbDialog) {
          this._initDialog(this.lbDialog);
          var prev = this.lbDialog.querySelector('.bq-modal__nav--prev');
          var next = this.lbDialog.querySelector('.bq-modal__nav--next');
          if (prev) prev.addEventListener('click', () => this.lightboxNav(-1));
          if (next) next.addEventListener('click', () => this.lightboxNav(1));
          document.addEventListener('keydown', (e) => {
            if (!this.lbDialog.open) return;
            if (e.key === 'ArrowLeft') this.lightboxNav(-1);
            else if (e.key === 'ArrowRight') this.lightboxNav(1);
          });
        }

        if (!this.browseOnly) {
          if (this.els.back) this.els.back.addEventListener('click', () => this.goBack());
          if (this.els.help) this.els.help.addEventListener('click', () => this.showFallback());
        }

        this.productImages = {};
        try {
          this.productImages = JSON.parse(this.dataset.productImages || '{}');
        } catch (e) {}
        this.t = {};
        try {
          this.t = JSON.parse(this.dataset.translations || '{}');
        } catch (e) {}
        this.loadData();
      }

      /* ── Shared dialog helpers ── */

      _initDialog(dialog) {
        // Close button
        var closeBtn = dialog.querySelector('.bq-modal__close');
        if (closeBtn) closeBtn.addEventListener('click', () => dialog.close());

        // Click-outside-to-close: clicks on the <dialog> element itself
        // (not its children) hit the backdrop area.
        dialog.addEventListener('click', (e) => {
          if (e.target === dialog) dialog.close();
        });
      }

      /* ── Data loading ── */

      async loadData() {
        try {
          var resp = await fetch(this.dataset.quizUrl);
          if (!resp.ok) throw new Error('Failed to load');
          this.data = await resp.json();
          if (!this.browseOnly) {
            this.initScores();
            this.startQuiz();
          }
          this.initBrowse();
        } catch (err) {
          console.error('[BottleQuiz]', err);
          if (this.els.steps) {
            this.els.steps.innerHTML =
              '<p class="bottle-quiz__error">' + (this.t.error || 'Sorry, the bottle finder could not load. Please try refreshing the page.') + '</p>';
          }
        }
      }

      initScores() {
        // Build flat bottle list with resolved traits (bottle traits override family)
        this.bottleList = [];
        for (var i = 0; i < this.data.families.length; i++) {
          var fam = this.data.families[i];
          var famTraits = fam.traits || {};
          var bottles = fam.bottles || [];
          for (var j = 0; j < bottles.length; j++) {
            var b = bottles[j];
            // Merge: bottle traits override family traits
            var merged = {};
            for (var k in famTraits) merged[k] = famTraits[k];
            if (b.traits) { for (var k in b.traits) merged[k] = b.traits[k]; }
            this.bottleList.push({
              id: b.id,
              bottle: b,
              family: fam,
              traits: merged,
              score: 50
            });
          }
        }
      }

      startQuiz() {
        this.answers = {};
        this.history = [];
        this.questionIndex = 0;
        this.initScores();
        this.els.result.hidden = true;
        this.els.steps.style.display = '';
        this.querySelector('.bottle-quiz__nav').style.display = '';
        if (this.els.candidates) this.els.candidates.hidden = true;
        this.showQuestion();
      }

      showQuestion() {
        var q = this.getNextQuestion();
        if (!q) { this.showResult(); return; }
        if (this.shouldAutoFinish()) { this.showResult(); return; }
        this.questionIndex = this.data.questions.indexOf(q);
        this.updateProgress();
        this.renderQuestion(q);
        this.renderCandidates();
        this.els.back.disabled = this.history.length === 0;
      }

      getNextQuestion() {
        for (var i = this.questionIndex; i < this.data.questions.length; i++) {
          var q = this.data.questions[i];
          if (!this.answers[q.id] && this.isRelevant(q)) return q;
        }
        return null;
      }

      isRelevant(q) {
        if (!this.bottleList || this.bottleList.length < 2) return true;
        var first = this.getTraitValue(this.bottleList[0], q.trait);
        for (var i = 1; i < this.bottleList.length; i++) {
          var val = this.getTraitValue(this.bottleList[i], q.trait);
          if (JSON.stringify(val) !== JSON.stringify(first)) return true;
        }
        return false;
      }

      getTraitValue(entry, trait) {
        return entry.traits ? entry.traits[trait] : undefined;
      }

      shouldAutoFinish() {
        if (!this.bottleList || this.bottleList.length < 2) return false;
        var bestScore = 0;
        var bestIdx = 0;
        for (var i = 0; i < this.bottleList.length; i++) {
          if (this.bottleList[i].score > bestScore) {
            bestScore = this.bottleList[i].score;
            bestIdx = i;
          }
        }
        var threshold = this.data.confidence_threshold || 85;
        var gap = this.data.confidence_gap || 30;
        if (bestScore < threshold) return false;
        for (var j = 0; j < this.bottleList.length; j++) {
          if (j !== bestIdx && bestScore - this.bottleList[j].score < gap) return false;
        }
        return true;
      }

      selectAnswer(questionId, value) {
        this.answers[questionId] = value;
        this.history.push({ questionId: questionId, index: this.questionIndex });
        this.applyScoring(questionId, value);
        this.questionIndex++;
        this.showQuestion();
      }

      applyScoring(questionId, value) {
        var q = this.data.questions.find(function (x) { return x.id === questionId; });
        if (!q || !q.scoring) return;
        var scoring = q.scoring;
        var neutralValues = scoring.neutral || [];
        if (neutralValues.indexOf(value) !== -1) return;

        for (var i = 0; i < this.bottleList.length; i++) {
          var entry = this.bottleList[i];
          var trait = this.getTraitValue(entry, q.trait);

          if (q.type === 'measurement' && scoring.ranges) {
            // Measurement ranges are still per-family
            if (value === scoring.no_match_value) {
              entry.score = Math.max(0, entry.score + scoring.mismatch);
            } else if (scoring.ranges[entry.family.id] === value) {
              entry.score = Math.min(100, entry.score + scoring.match);
            } else {
              entry.score = Math.max(0, entry.score + scoring.mismatch);
            }
          } else if (Array.isArray(trait)) {
            if (trait.indexOf(value) !== -1) {
              entry.score = Math.min(100, entry.score + scoring.match);
            } else {
              entry.score = Math.max(0, entry.score + scoring.mismatch);
            }
          } else {
            if (String(trait) === value) {
              entry.score = Math.min(100, entry.score + scoring.match);
            } else {
              entry.score = Math.max(0, entry.score + scoring.mismatch);
            }
          }
        }
      }

      goBack() {
        if (this.history.length === 0) return;
        var prev = this.history.pop();
        delete this.answers[prev.questionId];
        this.questionIndex = prev.index;
        this.initScores();
        var self = this;
        Object.keys(this.answers).forEach(function (qid) {
          self.applyScoring(qid, self.answers[qid]);
        });
        this.els.result.hidden = true;
        this.els.steps.style.display = '';
        this.querySelector('.bottle-quiz__nav').style.display = '';
        this.showQuestion();
      }

      updateProgress() {
        if (!this.els.progress) return;
        var answered = this.history.length;
        var total = this.data.questions.length;
        var pct = Math.round(((answered + 1) / (total + 1)) * 100);
        this.els.progress.style.width = pct + '%';
      }

      renderQuestion(q) {
        var html = '<div class="bottle-quiz__step">';
        html += '<h3 class="bottle-quiz__question">' + this.esc(this.i(q.question)) + '</h3>';
        if (q.help_text) {
          html += '<p class="bottle-quiz__help-text">' + this.esc(this.i(q.help_text)) + '</p>';
        }
        html += '<div class="bottle-quiz__options">';
        for (var i = 0; i < q.options.length; i++) {
          var opt = q.options[i];
          html += '<button type="button" class="bottle-quiz__card" data-answer="';
          html += this.escAttr(opt.value) + '" tabindex="0">';
          html += '<div class="bottle-quiz__card-content">';
          html += '<span class="bottle-quiz__card-label">' + this.esc(this.i(opt.label)) + '</span>';
          html += '<span class="bottle-quiz__card-desc">' + this.esc(this.i(opt.description)) + '</span>';
          html += '</div></button>';
        }
        html += '</div></div>';
        this.els.steps.innerHTML = html;
        this.els.steps.style.display = '';
        var self = this;
        this.els.steps.querySelectorAll('[data-answer]').forEach(function (btn) {
          btn.addEventListener('click', function () {
            self.selectAnswer(q.id, btn.dataset.answer);
          });
        });
      }

      renderCandidates() {
        var container = this.els.candidates;
        if (!container) return;
        if (this.history.length === 0) { container.hidden = true; return; }
        container.hidden = false;
        var ranked = this.getRankedBottles();
        var html = '<h4 class="bottle-quiz__candidates-title">' + (this.t.possible_matches || 'Possible matches') + '</h4>';
        html += '<div class="bottle-quiz__candidate-cards">';
        for (var i = 0; i < ranked.length; i++) {
          var entry = ranked[i];
          var b = entry.bottle;
          var fam = entry.family;
          var score = entry.score;
          if (b.hidden) continue;
          var low = score < 20;
          var imgUrl = b.image || this.productImages[fam.product_handle] || '';
          html += '<div class="bottle-quiz-browse__card bottle-quiz-browse__card--compact bottle-quiz__candidate-card' + (low ? ' bottle-quiz__candidate--low' : '') + '"';
          html += ' data-family-id="' + this.escAttr(fam.id) + '"';
          html += ' data-bottle-id="' + this.escAttr(b.id) + '">';
          html += '<div class="bottle-quiz__candidate-score-top">';
          html += '<div class="bottle-quiz__confidence-bar">';
          html += '<div class="bottle-quiz__confidence-fill" style="width:' + score + '%"></div>';
          html += '</div>';
          html += '</div>';
          html += '<div class="bottle-quiz-browse__card-img-wrap">';
          if (imgUrl) {
            html += '<img class="bottle-quiz-browse__card-img" src="' + this.escAttr(imgUrl) + '"';
            html += ' alt="' + this.escAttr(b.name || b.brand) + '" loading="lazy" width="200" height="200">';
          }
          html += '</div>';
          html += '<div class="bottle-quiz-browse__card-body">';
          html += '<div class="bottle-quiz-browse__card-name">' + this.esc(b.name || b.brand) + '</div>';
          html += '<div class="bottle-quiz-browse__card-specs">';
          html += (this.t.sizes || 'Sizes:') + ' ' + this.esc((b.sizes || fam.sizes).join(', '));
          html += '</div>';
          html += '</div></div>';
        }
        html += '</div>';
        container.innerHTML = html;

        // Wire up candidate card clicks to open lightbox
        var self = this;
        container.querySelectorAll('.bottle-quiz__candidate-card').forEach(function(card) {
          card.addEventListener('click', function() {
            var familyId = card.dataset.familyId;
            var bottleId = card.dataset.bottleId;
            var family = null;
            for (var fi = 0; fi < self.data.families.length; fi++) {
              if (self.data.families[fi].id === familyId) { family = self.data.families[fi]; break; }
            }
            if (!family) return;
            var bottle = null;
            var bottles = family.bottles || [];
            for (var bi = 0; bi < bottles.length; bi++) {
              if (bottles[bi].id === bottleId) { bottle = bottles[bi]; break; }
            }
            if (!bottle) return;
            var gallery = [];
            var heroImg = bottle.image || '';
            var sizeImgs = bottle.images || {};
            var bSizes = bottle.sizes || family.sizes;
            var gInfo = { name: bottle.name || bottle.brand, holder: family.name, url: family.product_url, familyId: family.id, bottleSizes: bSizes, holderSize: bottle.holder_size || null };
            if (heroImg) gallery.push({ src: heroImg, label: bottle.name || bottle.brand, info: gInfo });
            for (var si = 0; si < bSizes.length; si++) {
              var sz = bSizes[si];
              var szImg = sizeImgs[sz] || '';
              if (szImg && szImg !== heroImg) gallery.push({ src: szImg, label: (bottle.name || bottle.brand) + ' \u2013 ' + sz, size: sz, info: gInfo });
              else if (!szImg && heroImg && bSizes.length > 1) gallery.push({ src: heroImg, label: (bottle.name || bottle.brand) + ' \u2013 ' + sz, size: sz, info: gInfo });
            }
            var seen = {};
            var deduped = [];
            for (var di = 0; di < gallery.length; di++) {
              if (!seen[gallery[di].src + '|' + (gallery[di].size || '')]) {
                seen[gallery[di].src + '|' + (gallery[di].size || '')] = true;
                deduped.push(gallery[di]);
              }
            }
            if (deduped.length === 0) {
              deduped.push({ src: '', label: bottle.name || bottle.brand, info: gInfo });
            }
            self.openLightbox(deduped, 0, familyId);
          });
        });
      }

      getRankedBottles() {
        var sorted = this.bottleList.slice().sort(function (a, b) { return b.score - a.score; });
        return sorted;
      }

      getRankedFamilies() {
        // Aggregate: best bottle score per family
        var byFamily = {};
        for (var i = 0; i < this.bottleList.length; i++) {
          var e = this.bottleList[i];
          if (!byFamily[e.family.id] || e.score > byFamily[e.family.id].score) {
            byFamily[e.family.id] = { family: e.family, score: e.score };
          }
        }
        var ranked = [];
        for (var fid in byFamily) ranked.push(byFamily[fid]);
        ranked.sort(function (a, b) { return b.score - a.score; });
        return ranked;
      }

      showResult() {
        this.els.steps.style.display = 'none';
        // keep nav visible on result screen
        if (this.els.back) this.els.back.disabled = false;
        if (this.els.candidates) this.els.candidates.hidden = true;
        if (this.els.progress) this.els.progress.style.width = '100%';
        this.els.result.hidden = false;
        var rankedBottles = this.getRankedBottles();
        var bestBottle = rankedBottles[0];
        var ranked = this.getRankedFamilies();
        if (bestBottle.score < 30) {
          this.renderNoMatch();
        } else {
          this.renderMatch(bestBottle.family, bestBottle.score, ranked, bestBottle);
        }
      }

      renderMatch(family, score, ranked, bestBottle) {
        var confident = score >= (this.data.confidence_threshold || 85);
        // Use the best-matching bottle's image, fall back to Shopify product image
        var imgUrl = (bestBottle && bestBottle.bottle.image) ? bestBottle.bottle.image : '';
        if (!imgUrl) {
          var bottles = family.bottles || [];
          for (var bi = 0; bi < bottles.length; bi++) {
            if (!bottles[bi].hidden && bottles[bi].image) { imgUrl = bottles[bi].image; break; }
          }
        }
        if (!imgUrl) imgUrl = this.productImages[family.product_handle] || '';
        var html = '<div class="bottle-quiz__result-inner">';
        if (confident) {
          html += '<div class="bottle-quiz__result-badge bottle-quiz__result-badge--match">';
          html += '<span>' + (this.t.match_found || 'Match found') + ' - ' + score + '% ' + (this.t.confidence || 'confidence') + '</span></div>';
        } else {
          html += '<div class="bottle-quiz__result-badge bottle-quiz__result-badge--partial">';
          html += '<span>' + (this.t.likely_match || 'Likely match') + ' - ' + score + '% ' + (this.t.confidence || 'confidence') + '</span></div>';
        }
        html += '<h3 class="bottle-quiz__result-title">' + this.esc(family.name) + ' ' + (this.t.bottle_holder || 'bottle holder') + '</h3>';
        if (imgUrl) {
          html += '<img class="bottle-quiz__result-img" src="' + this.escAttr(imgUrl);
          html += '" alt="' + this.escAttr(family.name) + '" width="200" height="200">';
        }
        html += '<p class="bottle-quiz__result-desc">' + this.esc(this.i(family.description)) + '</p>';
        html += '<div class="bottle-quiz__result-brands">';
        html += '<span class="bottle-quiz__result-brands-label">' + (this.t.compatible_brands || 'Compatible brands:') + '</span> ';
        html += this.esc(family.brands.join(', ')) + '</div>';
        html += '<div class="bottle-quiz__result-specs">';
        html += '<span>' + (this.t.sizes || 'Sizes:') + ' ' + this.esc(family.sizes.join(', ')) + '</span></div>';
        html += '<button type="button" class="bottle-quiz__result-cta button button--primary"';
        html += ' onclick="this.closest(\x27bottle-quiz\x27).openResultModal(\x27' + this.escAttr(family.id) + '\x27)">';
        html += (this.t.choose_setup || 'Choose your setup') + '</button>';
        if (!confident) {
          html += '<p class="bottle-quiz__result-caveat">' + (this.t.not_sure || 'Not 100% sure?') + ' ';
          html += '<button type="button" class="link"';
          html += " onclick=\"this.closest('bottle-quiz').showFallback()\">";
          html += (this.t.send_photo || 'Send us a photo') + '</button> ' + (this.t.send_photo_confirm || 'and we will confirm before you order.') + '</p>';
        }
        var altBottles = this.getRankedBottles();
        var altHtml = '';
        var altCount = 0;
        for (var ai = 0; ai < altBottles.length; ai++) {
          var ae = altBottles[ai];
          if (ae === bestBottle) continue;
          if (ae.bottle.hidden) continue;
          if (ae.score < 10) continue;
          if (altCount >= 2) break;
          var aeImg = ae.bottle.image || this.productImages[ae.family.product_handle] || '';
          altHtml += '<div class="bottle-quiz-browse__card bottle-quiz-browse__card--compact bottle-quiz__candidate-card bottle-quiz__result-alt-card"';
          altHtml += ' data-family-id="' + this.escAttr(ae.family.id) + '"';
          altHtml += ' data-bottle-id="' + this.escAttr(ae.bottle.id) + '">';
          altHtml += '<div class="bottle-quiz__candidate-score-top">';
          altHtml += '<div class="bottle-quiz__confidence-bar"><div class="bottle-quiz__confidence-fill" style="width:' + ae.score + '%"></div></div>';
          altHtml += '</div>';
          altHtml += '<div class="bottle-quiz-browse__card-img-wrap">';
          if (aeImg) {
            altHtml += '<img class="bottle-quiz-browse__card-img" src="' + this.escAttr(aeImg) + '"';
            altHtml += ' alt="' + this.escAttr(ae.bottle.name || ae.bottle.brand) + '" loading="lazy" width="200" height="200">';
          }
          altHtml += '</div>';
          altHtml += '<div class="bottle-quiz-browse__card-body">';
          altHtml += '<div class="bottle-quiz-browse__card-name">' + this.esc(ae.bottle.name || ae.bottle.brand) + '</div>';
          altHtml += '<div class="bottle-quiz-browse__card-specs">' + (this.t.sizes || 'Sizes:') + ' ' + this.esc((ae.bottle.sizes || ae.family.sizes).join(', ')) + '</div>';
          altHtml += '</div></div>';
          altCount++;
        }
        if (altCount > 0) {
          html += '<div class="bottle-quiz__result-crossref">';
          html += '<p class="bottle-quiz__result-crossref-title">' + (this.t.other_matches || 'Other possible matches:') + '</p>';
          html += '<div class="bottle-quiz__result-crossref-cards">' + altHtml + '</div>';
          html += '</div>';
        }
        html += '<button type="button" class="bottle-quiz__restart button button--secondary"';
        html += " onclick=\"this.closest('bottle-quiz').startQuiz()\">" + (this.t.start_over || 'Start over') + '</button>';
        html += '</div>';
        this.els.result.innerHTML = html;
      }

      renderNoMatch() {
        var email = this.dataset.fallbackEmail;
        var msg = (this.i(this.data.unsupported_note) || '').replace('{email}', email);
        var html = '<div class="bottle-quiz__result-inner">';
        html += '<div class="bottle-quiz__result-badge bottle-quiz__result-badge--none">';
        html += '<span>' + (this.t.no_match || 'No match yet') + '</span></div>';
        html += '<h3 class="bottle-quiz__result-title">' + (this.t.no_holder_title || 'We do not have a holder for this bottle yet') + '</h3>';
        html += '<p class="bottle-quiz__result-desc">' + this.esc(msg) + '</p>';
        html += '<button type="button" class="bottle-quiz__result-cta button button--primary"';
        html += " onclick=\"this.closest('bottle-quiz').showFallback()\">";
        html += (this.t.send_photo_bottle || 'Send us a photo of your bottle') + '</button>';
        html += '<button type="button" class="bottle-quiz__restart link"';
        html += " onclick=\"this.closest('bottle-quiz').startQuiz()\">" + (this.t.start_over || 'Start over') + '</button>';
        html += '</div>';
        this.els.result.innerHTML = html;
      }

      openResultModal(familyId) {
        var family = null;
        for (var i = 0; i < this.data.families.length; i++) {
          if (this.data.families[i].id === familyId) { family = this.data.families[i]; break; }
        }
        if (!family) return;
        // Build gallery from first non-hidden bottle
        var gallery = [];
        var bottles = family.bottles || [];
        for (var j = 0; j < bottles.length; j++) {
          if (!bottles[j].hidden && bottles[j].image) {
            var b = bottles[j];
            var gInfo = { name: b.name || b.brand, holder: family.name, url: family.product_url, familyId: family.id, bottleSizes: b.sizes || family.sizes };
            gallery.push({ src: b.image, label: b.name || b.brand, info: gInfo });
            break;
          }
        }
        if (gallery.length === 0) {
          var imgUrl = this.productImages[family.product_handle] || '';
          var gInfo = { name: family.name, holder: family.name, url: family.product_url, familyId: family.id, bottleSizes: family.sizes };
          gallery.push({ src: imgUrl, label: family.name, info: gInfo });
        }
        this.openLightbox(gallery, 0, familyId);
      }

      showFallback() {
        if (this.helpDialog) this.helpDialog.showModal();
      }

      openInbox() {
        var self = this;
        var tries = 0;
        (function attempt() {
          if (window.Shopify && window.Shopify.chat && typeof window.Shopify.chat.open === 'function') {
            try { window.Shopify.chat.open(); return; } catch (e) {}
          }
          var sels = [
            '#ShopifyChatLauncher',
            '[id^="shopify-chat"] button',
            'shopify-chat-widget',
            'iframe[title="Chat window"]',
            'iframe[name="shopify-chat-iframe"]'
          ];
          for (var i = 0; i < sels.length; i++) {
            var el = document.querySelector(sels[i]);
            if (el) {
              if (el.tagName === 'IFRAME') {
                try { el.contentWindow.postMessage({ type: 'open' }, '*'); return; } catch (e) {}
              } else if (typeof el.click === 'function') {
                el.click(); return;
              }
            }
          }
          if (++tries < 8) {
            setTimeout(attempt, 400);
          } else {
            self.showFallback();
          }
        })();
      }

      /* ── Browse list ── */

      initBrowse() {
        var wrapper = this.closest('.bottle-quiz-wrapper');
        if (!wrapper) return;
        this.browseGrid = wrapper.querySelector('.bottle-quiz-browse__grid');
        this.browseNoResults = wrapper.querySelector('.bottle-quiz-browse__no-results');
        this.browseSearch = wrapper.querySelector('.bottle-quiz-browse__search');
        if (!this.browseGrid) return;
        this.renderBrowseList();
        if (this.browseSearch) {
          var self = this;
          this.browseSearch.addEventListener('input', function () {
            self.filterBrowse(this.value);
          });
        }
      }

      getBrowseItems() {
        var items = [];
        for (var i = 0; i < this.data.families.length; i++) {
          var fam = this.data.families[i];
          var bottles = fam.bottles || [];
          if (bottles.length === 0) {
            items.push({
              id: fam.id, name: fam.name, brand: fam.brands.join(', '),
              description: fam.description, sizes: fam.sizes,
              diameter_mm: fam.diameter_mm, product_url: fam.product_url,
              product_handle: fam.product_handle,
              search_terms: fam.search_terms || [], family: fam
            });
          } else {
            for (var j = 0; j < bottles.length; j++) {
              var b = bottles[j];
              if (b.hidden) continue;
              items.push({
                id: b.id, name: b.name || b.brand, brand: b.brand,
                description: fam.description, sizes: b.sizes || fam.sizes,
                diameter_mm: fam.diameter_mm, product_url: fam.product_url,
                product_handle: fam.product_handle,
                image: b.image || '',
                images: b.images || {},
                source_url: b.source_url || '',
                source_name: b.source_name || '',
                search_terms: (b.search_terms || []).concat(fam.search_terms || []),
                holderSize: b.holder_size || null,
                family: fam
              });
            }
          }
        }
        return items;
      }

      renderBrowseList() {
        if (!this.browseGrid) return;
        this.browseItems = this.getBrowseItems();
        var html = '';
        var arrowLeft = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m15 18-6-6 6-6"/></svg>';
        var arrowRight = '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 18 6-6-6-6"/></svg>';
        for (var i = 0; i < this.browseItems.length; i++) {
          var item = this.browseItems[i];
          var heroImg = item.image || this.productImages[item.product_handle] || '';
          var gallery = [];
          var gInfo = { name: item.name, holder: item.family.name, url: item.product_url, familyId: item.family.id, bottleSizes: item.sizes, holderSize: item.holderSize || null };
          if (heroImg) gallery.push({ src: heroImg, label: item.name, size: null, info: gInfo });
          var sizeImgs = item.images || {};
          for (var si = 0; si < item.sizes.length; si++) {
            var sz = item.sizes[si];
            var szImg = sizeImgs[sz] || '';
            if (szImg) gallery.push({ src: szImg, label: item.name + ' – ' + sz, size: sz, info: gInfo });
            else if (heroImg) gallery.push({ src: heroImg, label: item.name + ' – ' + sz, size: sz, info: gInfo });
          }
          // Deduplicate by src
          var dedupGallery = [];
          var seenSrc = {};
          for (var gi = 0; gi < gallery.length; gi++) {
            if (!seenSrc[gallery[gi].src]) {
              seenSrc[gallery[gi].src] = true;
              dedupGallery.push(gallery[gi]);
            }
          }
          gallery = dedupGallery;
          var imgUrl = gallery.length > 0 ? gallery[0].src : '';
          html += '<div class="bottle-quiz-browse__card" data-browse-idx="' + i + '">';
          html += '<div class="bottle-quiz-browse__card-img-wrap"';
          if (imgUrl) {
            html += ' data-gallery=\'' + this.escAttr(JSON.stringify(gallery)) + '\'';
            html += ' data-gallery-idx="0"';
          }
          html += '>';
          if (imgUrl) {
            html += '<img class="bottle-quiz-browse__card-img" src="' + this.escAttr(imgUrl) + '"';
            html += ' alt="' + this.escAttr(item.name) + '" loading="lazy" width="200" height="200">';
            html += '<span class="bottle-quiz-browse__zoom-icon" aria-label="Zoom image">';
            html += '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor"';
            html += ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round">';
            html += '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>';
            html += '<path d="M11 8v6"/><path d="M8 11h6"/></svg>';
            html += '</span>';
            if (gallery.length > 1) {
              html += '<button type="button" class="bottle-quiz-browse__arrow bottle-quiz-browse__arrow--prev" aria-label="Previous">' + arrowLeft + '</button>';
              html += '<button type="button" class="bottle-quiz-browse__arrow bottle-quiz-browse__arrow--next" aria-label="Next">' + arrowRight + '</button>';
              html += '<span class="bottle-quiz-browse__img-label">' + this.esc(gallery[0].label) + '</span>';
            }
          }
          html += '</div>';
          html += '<div class="bottle-quiz-browse__card-body">';
          html += '<div class="bottle-quiz-browse__card-name">' + this.esc(item.name) + '</div>';
          html += '<div class="bottle-quiz-browse__card-specs">';
          html += (this.t.sizes || 'Sizes:') + ' ' + this.esc(item.sizes.join(', '));
          html += '</div>';
          html += '<div class="bottle-quiz-browse__card-holder">';
          html += (this.t.fits_holder || 'Fits the {{ holder }} holder').replace('{{ holder }}', '<strong>' + this.esc(item.family.name) + '</strong>') + '</div>';
          html += '<button type="button" class="bottle-quiz-browse__card-link bottle-quiz-browse__choose-app button button--primary"';
          html += ' data-family-id="' + this.escAttr(item.family.id) + '">';
          html += (this.t.choose_setup || 'Choose your setup') + '</button>';
          if (item.source_url) {
            html += '<a href="' + this.escAttr(item.source_url) + '" class="bottle-quiz-browse__card-source"';
            html += ' target="_blank" rel="noopener noreferrer">';
            html += '<svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor"';
            html += ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round">';
            html += '<path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>';
            html += '<polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>';
            html += ' ' + this.esc(item.source_name || 'Source') + '</a>';
          }
          html += '</div></div>';
        }
        this.browseGrid.innerHTML = html;
        var self = this;

        // Zoom click handler
        this.browseGrid.querySelectorAll('.bottle-quiz-browse__card-img-wrap').forEach(function (wrap) {
          wrap.addEventListener('click', function (e) {
            if (e.target.closest('.bottle-quiz-browse__arrow')) return;
            var galleryData = wrap.dataset.gallery;
            if (!galleryData) return;
            var gallery = JSON.parse(galleryData);
            var idx = parseInt(wrap.dataset.galleryIdx || '0', 10);
            self.openLightbox(gallery, idx);
          });
        });

        // Card arrow click handlers
        this.browseGrid.querySelectorAll('.bottle-quiz-browse__arrow').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.stopPropagation();
            var wrap = btn.closest('.bottle-quiz-browse__card-img-wrap');
            if (!wrap) return;
            var gallery = JSON.parse(wrap.dataset.gallery || '[]');
            if (gallery.length < 2) return;
            var idx = parseInt(wrap.dataset.galleryIdx || '0', 10);
            if (btn.classList.contains('bottle-quiz-browse__arrow--next')) {
              idx = (idx + 1) % gallery.length;
            } else {
              idx = (idx - 1 + gallery.length) % gallery.length;
            }
            wrap.dataset.galleryIdx = idx;
            var img = wrap.querySelector('.bottle-quiz-browse__card-img');
            var label = wrap.querySelector('.bottle-quiz-browse__img-label');
            if (img) {
              img.src = gallery[idx].src;
              img.alt = gallery[idx].label;
            }
            if (label) label.textContent = gallery[idx].label;
          });
        });

        // "Choose your setup" button handler
        this.browseGrid.querySelectorAll('.bottle-quiz-browse__choose-app').forEach(function (btn) {
          btn.addEventListener('click', function (e) {
            e.preventDefault();
            var card = btn.closest('.bottle-quiz-browse__card');
            var wrap = card ? card.querySelector('.bottle-quiz-browse__card-img-wrap') : null;
            var familyId = btn.dataset.familyId;
            var gallery = [];
            if (wrap && wrap.dataset.gallery) {
              gallery = JSON.parse(wrap.dataset.gallery);
            }
            if (gallery.length > 0) {
              self.openLightbox(gallery, 0, familyId);
            } else {
              // No image — open with empty gallery, just show apps
              self.openLightbox([{ src: '', label: '', info: { familyId: familyId } }], 0, familyId);
            }
          });
        });
      }

      filterBrowse(query) {
        var q = query.toLowerCase().trim();
        var cards = this.browseGrid.querySelectorAll('.bottle-quiz-browse__card');
        var visible = 0;
        for (var i = 0; i < cards.length; i++) {
          var card = cards[i];
          var idx = parseInt(card.dataset.browseIdx, 10);
          var item = this.browseItems[idx];
          if (!item) { card.style.display = 'none'; continue; }
          var haystack = [
            item.name, item.brand, item.description,
            item.sizes.join(' '), item.search_terms.join(' ')
          ].join(' ').toLowerCase();
          if (!q || haystack.indexOf(q) !== -1) {
            card.style.display = '';
            visible++;
          } else {
            card.style.display = 'none';
          }
        }
        if (this.browseNoResults) {
          this.browseNoResults.hidden = visible > 0 || !q;
        }
      }

      /* ── Lightbox ── */

      openLightbox(gallery, startIdx, familyId) {
        if (!this.lbDialog) return;
        this.lbGallery = gallery;
        this.lbIdx = startIdx || 0;
        this.lbFamilyId = familyId || (gallery[0] && gallery[0].info ? gallery[0].info.familyId : null);
        this.updateLightboxImage();
        this.renderApplications();
        this.lbDialog.showModal();
      }

      updateLightboxImage() {
        var img = this.lbDialog.querySelector('.bq-modal__img');
        var caption = this.lbDialog.querySelector('.bq-modal__caption');
        var lbTitle = this.lbDialog.querySelector('.bq-modal__lb-title');
        var prev = this.lbDialog.querySelector('.bq-modal__nav--prev');
        var next = this.lbDialog.querySelector('.bq-modal__nav--next');
        var item = this.lbGallery[this.lbIdx];
        img.src = item.src.replace('width=200', 'width=800');
        img.alt = item.label || '';
        if (lbTitle) {
          lbTitle.textContent = item.label || '';
        }
        if (caption) {
          var info = item.info || {};
          var ch = '';
          if (info.holder) {
            ch += '<span class="bq-modal__caption-holder">' + (this.t.fits_holder || 'Fits the {{ holder }} holder').replace('{{ holder }}', '<strong>' + this.esc(info.holder) + '</strong>') + '</span>';
          }
          if (info.url) {
            var captionRoot = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
            var captionUrl = info.url.startsWith('/') ? captionRoot + info.url.slice(1) : info.url;
            ch += '<a href="' + this.escAttr(captionUrl) + '" class="bq-modal__caption-link" target="_blank" rel="noopener">';
            ch += (this.t.view_holder || 'View {{ holder }} holder →').replace('{{ holder }}', this.esc(info.holder || '')) + '</a>';
          }
          caption.innerHTML = ch;
        }
        var multi = this.lbGallery.length > 1;
        if (prev) prev.hidden = !multi;
        if (next) next.hidden = !multi;
      }

      lightboxNav(dir) {
        if (!this.lbGallery || this.lbGallery.length < 2) return;
        this.lbIdx = (this.lbIdx + dir + this.lbGallery.length) % this.lbGallery.length;
        this.updateLightboxImage();
        this.renderApplications();
      }

      /* ── Application picker ── */

      renderApplications() {
        var container = this.lbDialog.querySelector('.bq-modal__apps');
        if (!container) return;
        if (!this.lbFamilyId || !this.data) { container.innerHTML = ''; return; }

        var family = null;
        for (var i = 0; i < this.data.families.length; i++) {
          if (this.data.families[i].id === this.lbFamilyId) {
            family = this.data.families[i]; break;
          }
        }
        if (!family || !family.applications) { container.innerHTML = ''; return; }

        var apps = this.data.applications || [];
        var famApps = family.applications;
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

        // Determine which sizes to check: use bottle-specific sizes if available
        var currentItem = this.lbGallery && this.lbGallery[this.lbIdx] ? this.lbGallery[this.lbIdx] : null;
        var currentSize = currentItem ? currentItem.size : null;
        var bottleSizes = (currentItem && currentItem.info && currentItem.info.bottleSizes) ? currentItem.info.bottleSizes : family.sizes;

        // If bottle has a fixed holder_size mapping, use that instead
        var holderSize = (currentItem && currentItem.info && currentItem.info.holderSize) ? currentItem.info.holderSize : null;
        if (holderSize) currentSize = holderSize;

        // Find first available app image for default preview
        var defaultImg = '';
        for (var d = 0; d < apps.length; d++) {
          if (!apps[d].coming_soon && famApps[apps[d].id] && famApps[apps[d].id].image) {
            defaultImg = famApps[apps[d].id].image; break;
          }
        }

        var html = '<div class="bq-modal__apps-title">' + (this.t.where_use || 'Where will you use this holder?') + '</div>';

        // Holder preview image
        if (defaultImg) {
          html += '<div class="bq-modal__apps-preview">';
          html += '<img class="bq-modal__apps-preview-img" src="' + this.escAttr(defaultImg) + '" alt="' + this.escAttr(this.t.holder_preview_alt || 'Holder preview') + '">';
          html += '</div>';
        }

        html += '<div class="bq-modal__apps-grid">';

        for (var a = 0; a < apps.length; a++) {
          var app = apps[a];
          var famApp = famApps[app.id];
          var available = !!famApp && !app.coming_soon;
          var appImg = (famApp && famApp.image) ? famApp.image : (app.image || '');

          if (app.coming_soon) {
            html += '<div class="bq-modal__app-card bq-modal__app-card--soon"';
            if (appImg) html += ' data-preview="' + this.escAttr(appImg) + '"';
            html += '>';
            html += '<div class="bq-modal__app-card-name">' + this.esc(this.i(app.name)) + '</div>';
            html += '<span class="bq-modal__app-card-badge">' + (this.t.coming_soon || 'Coming soon') + '</span>';
            html += '</div>';
          } else if (available) {
            var sizeUnavailable = currentSize && !famApp.variants[currentSize];

            var excludedSizes = [];
            if (!currentSize) {
              for (var s = 0; s < bottleSizes.length; s++) {
                if (!famApp.variants[bottleSizes[s]]) excludedSizes.push(bottleSizes[s]);
              }
            }

            var url = root + famApp.product_path;
            if (currentSize && famApp.variants[currentSize]) {
              url += '?variant=' + famApp.variants[currentSize];
            }

            if (sizeUnavailable) {
              html += '<div class="bq-modal__app-card bq-modal__app-card--unavailable"';
              if (appImg) html += ' data-preview="' + this.escAttr(appImg) + '"';
              html += '>';
              html += '<div class="bq-modal__app-card-name">' + this.esc(this.i(app.name)) + '</div>';
              html += '<span class="bq-modal__app-card-note">' + (this.t.not_available || 'Not available for {{ size }}').replace('{{ size }}', this.esc(currentSize)) + '</span>';
              html += '</div>';
            } else {
              html += '<a href="' + this.escAttr(url) + '" class="bq-modal__app-card"';
              if (appImg) html += ' data-preview="' + this.escAttr(appImg) + '"';
              html += ' target="_blank" rel="noopener">';
              html += '<div class="bq-modal__app-card-name">' + this.esc(this.i(app.name)) + '</div>';
              if (currentSize) {
                html += '<span class="bq-modal__app-card-note bq-modal__app-card-note--info">' + (this.t.holder_size || 'Holder size: {{ size }}').replace('{{ size }}', this.esc(currentSize)) + '</span>';
              }
              html += '</a>';
            }
          }
        }
        html += '</div>';
        container.innerHTML = html;

        // Wire up hover preview swap
        var previewImg = container.querySelector('.bq-modal__apps-preview-img');
        if (previewImg) {
          container.querySelectorAll('.bq-modal__app-card[data-preview]').forEach(function(card) {
            card.addEventListener('mouseenter', function() {
              previewImg.src = card.dataset.preview;
            });
            card.addEventListener('touchstart', function() {
              previewImg.src = card.dataset.preview;
            }, { passive: true });
          });
        }
      }

      /* ── URL helper ── */

      getAppUrl(familyId, appId) {
        if (!this.data) return '#';
        var family = null;
        for (var i = 0; i < this.data.families.length; i++) {
          if (this.data.families[i].id === familyId) { family = this.data.families[i]; break; }
        }
        if (!family || !family.applications || !family.applications[appId]) return '#';
        var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        return root + family.applications[appId].product_path;
      }

      esc(str) {
        var el = document.createElement('span');
        el.textContent = str;
        return el.innerHTML;
      }

      escAttr(str) {
        return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      }
    }
  );
}
